import styles from './MainSignupSection.module.scss';
import { SSOButton } from 'src/v2/pages/auth/components/SSOButton/SSOButton';
import { AppRoutes, GTMEvent, SSOProvider } from 'src/types';
import React, {
  useCallback,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import AuthContext from 'src/contexts/AuthContext';
import { EmailSubmitForm } from 'src/v2/pages/auth/components/EmailSubmitForm';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { sendGTMEvent } from 'src/utils';
import { isGoogleEmailProvider } from 'src/utils/user';
import { useLazyCheckEmailInfoQuery } from 'src/store/services';
import { GoogleSignupSuggestionSection } from 'src/v2/pages/auth/components/GoogleSignupSuggestionSection';
import { isEmailSignupEnabled, isBetaEnv } from 'src/utils';
import { TermsSection } from 'src/v2/pages/auth/components/TermsSection/TermsSection';
import { TextWithLinkSection } from 'src/v2/pages/auth/components/TextWithLinkSection';
import { StudentDiscountSignupMessage } from 'src/v2/pages/auth/components/StudentDiscountSignupMessage';
import { SN_UI_DOMAIN, UI_DOMAIN } from 'src/constants';
import { getLastLogin } from 'src/utils/lastLoginStorage';
import { EMAIL_LOGIN_PROVIDER } from 'src/types';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { env } from 'src/env';

export const MainSignupSection = () => {
  const navigate = useNavigate();
  const { setAuthLoading } = useContext(AuthContext);

  const isThanksgivingCouponEnabled =
    useFeatureFlagEnabled('thanksgivingCoupon');
  const isFacebookSigninDisabled = useFeatureFlagEnabled(
    'disable-facebook-signin',
  );
  const isMicrosoftSignUpDisabled = useFeatureFlagEnabled(
    'hide-microsoft-option',
  );
  const isCreditBasedPaymentEnabled = useFeatureFlagEnabled(
    'credit-based-system',
  );

  const [googlePromptEmail, setGooglePromptEmail] = useState('');
  const [showMore, setShowMore] = useState(false);
  const [lastLogin, setLastLogin] =
    useState<ReturnType<typeof getLastLogin>>(null);
  const [checkEmailInfo] = useLazyCheckEmailInfoQuery();

  useEffect(() => {
    const loginInfo = getLastLogin();
    setLastLogin(loginInfo);
  }, []);

  const focusedProvider = useMemo(() => {
    if (lastLogin && lastLogin.loginType !== EMAIL_LOGIN_PROVIDER) {
      return lastLogin.loginType as SSOProvider;
    }
    return null;
  }, [lastLogin]);

  const defaultEmail = useMemo(() => {
    if (
      isEmailSignupEnabled &&
      lastLogin &&
      lastLogin.loginType === EMAIL_LOGIN_PROVIDER
    ) {
      return lastLogin.email;
    }
    return undefined;
  }, [lastLogin]);

  const toggleShowMore = () => {
    setShowMore((prev) => !prev);
  };

  const handleEmailSubmit = useCallback(
    async (email: string) => {
      // Guard clause in case email signup is disabled
      if (!isEmailSignupEnabled) return;
      const encodedEmail = encodeURIComponent(email);
      const redirectToEmailSignUp = `${AppRoutes.SIGN_UP_WITH_EMAIL}?email=${encodedEmail}`;

      try {
        const { data } = await checkEmailInfo(email);

        sendGTMEvent(GTMEvent.EMAIL_SIGNUP_CLICKS);

        if (isGoogleEmailProvider(data?.provider)) {
          setGooglePromptEmail(email);
        } else {
          navigate(redirectToEmailSignUp);
        }
      } catch (error) {
        navigate(redirectToEmailSignUp);
      }
    },
    [checkEmailInfo, navigate],
  );
  if (googlePromptEmail) {
    return <GoogleSignupSuggestionSection email={googlePromptEmail} />;
  }

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={styles.signUpHeaderSection}>
          {isCreditBasedPaymentEnabled ? (
            <a
              href={env.REACT_APP_SUPER_NINJA_URL}
              className={styles.backToHome}
            >
              <span className={styles.backToHomeLink}>
                Back to {SN_UI_DOMAIN}
              </span>
            </a>
          ) : (
            <Link to={AppRoutes.HOME} className={styles.backToHome}>
              <span className={styles.backToHomeLink}>Back to {UI_DOMAIN}</span>
            </Link>
          )}
          <h2 className={styles.signUpHeader}>Sign up</h2>
        </div>
        <div className={styles.ssoSection}>
          <SSOButton
            ssoProvider={SSOProvider.GOOGLE}
            setAuthLoading={setAuthLoading}
            showFullButton={true}
            showLastUsedLabel={focusedProvider === SSOProvider.GOOGLE}
          />
          <SSOButton
            ssoProvider={SSOProvider.APPLE}
            setAuthLoading={setAuthLoading}
            showFullButton={true}
            showLastUsedLabel={focusedProvider === SSOProvider.APPLE}
          />
          {!isMicrosoftSignUpDisabled && (
            <SSOButton
              ssoProvider={SSOProvider.MICROSOFT}
              setAuthLoading={setAuthLoading}
              showFullButton={true}
              showLastUsedLabel={focusedProvider === SSOProvider.MICROSOFT}
            />
          )}
          {showMore ? (
            <>
              {!isFacebookSigninDisabled && (
                <SSOButton
                  ssoProvider={SSOProvider.FACEBOOK}
                  setAuthLoading={setAuthLoading}
                  showFullButton={true}
                  showLastUsedLabel={focusedProvider === SSOProvider.FACEBOOK}
                />
              )}
              <SSOButton
                ssoProvider={SSOProvider.AMAZON}
                setAuthLoading={setAuthLoading}
                showFullButton={true}
                showLastUsedLabel={focusedProvider === SSOProvider.AMAZON}
              />
              <SSOButton
                ssoProvider={SSOProvider.LINKEDIN}
                setAuthLoading={setAuthLoading}
                showFullButton={true}
                showLastUsedLabel={focusedProvider === SSOProvider.LINKEDIN}
              />
            </>
          ) : (
            <div className={styles.showMoreSection}>
              {!isFacebookSigninDisabled && (
                <SSOButton
                  ssoProvider={SSOProvider.FACEBOOK}
                  setAuthLoading={setAuthLoading}
                  smallButton={true}
                  showLastUsedLabel={focusedProvider === SSOProvider.FACEBOOK}
                />
              )}
              <SSOButton
                ssoProvider={SSOProvider.AMAZON}
                setAuthLoading={setAuthLoading}
                smallButton={true}
                showLastUsedLabel={focusedProvider === SSOProvider.AMAZON}
              />
              <SSOButton
                ssoProvider={SSOProvider.LINKEDIN}
                setAuthLoading={setAuthLoading}
                smallButton={true}
                showLastUsedLabel={focusedProvider === SSOProvider.LINKEDIN}
              />
              <button
                className={styles.showMoreButton}
                onClick={toggleShowMore}
              >
                Show more
              </button>
            </div>
          )}
        </div>
        {isBetaEnv && (
          <>
            <div className={styles.ssoSectionSeparator}>
              <div className={styles.lineSeparator} />
              <div>OR</div>
              <div className={styles.lineSeparator} />
            </div>
            <EmailSubmitForm
              onSubmit={handleEmailSubmit}
              defaultEmail={defaultEmail}
            />
          </>
        )}
        <TextWithLinkSection
          text="Already have an account?"
          linkText="Log in"
          linkRoute={AppRoutes.LOGIN}
        />

        <TermsSection />

        {!isThanksgivingCouponEnabled && !isCreditBasedPaymentEnabled && (
          <StudentDiscountSignupMessage />
        )}
      </div>
    </div>
  );
};
